import * as d3 from 'd3'

import { parseCalendarDate } from '@/utils'

interface CurrencyOptions {
  decimals?: number
  showCents?: boolean
}

interface NumberOptions {
  decimals?: number
}

export const formatCurrency = (
  value: number,
  options: CurrencyOptions = {},
): string => {
  const { decimals = 1, showCents = false } = options

  const abs = Math.abs(value)
  const sign = value < 0 ? '-' : ''

  if (abs >= 1_000_000_000) {
    return `${sign}$${(abs / 1_000_000_000).toFixed(decimals)}B`
  }
  if (abs >= 1_000_000) {
    return `${sign}$${(abs / 1_000_000).toFixed(decimals)}M`
  }
  if (abs >= 1_000) {
    return `${sign}$${(abs / 1_000).toFixed(decimals)}K`
  }

  if (showCents) {
    return `${sign}$${abs.toFixed(2)}`
  }

  return `${sign}$${abs}`
}

export const formatDate = (
  date: string | Date,
  format: string = '%b %Y',
): string => {
  if (typeof date === 'string') {
    const parsed = parseCalendarDate(date)
    if (Number.isNaN(parsed.getTime())) return date
    return d3.timeFormat(format)(parsed)
  }
  return d3.timeFormat(format)(date)
}

export const formatPercent = (value: number, decimals: number = 1): string => {
  return `${value.toFixed(decimals)}%`
}

export const formatNumber = (
  value: number,
  options: NumberOptions = {},
): string => {
  const { decimals = 0 } = options
  const abs = Math.abs(value)
  const sign = value < 0 ? '-' : ''

  if (abs >= 1_000_000_000) {
    return `${sign}${(abs / 1_000_000_000).toFixed(decimals)}B`
  }
  if (abs >= 1_000_000) {
    return `${sign}${(abs / 1_000_000).toFixed(decimals)}M`
  }
  if (abs >= 1_000) {
    return `${sign}${(abs / 1_000).toFixed(decimals)}K`
  }

  return `${sign}${abs}`
}

// Compact currency for chart labels: `$1.2M`, `$3.4K`, `$5` (signed).
// Differs from formatCurrency in that sub-$1K values are rounded to
// integers (chart labels never want fractional dollars below $1K).
export const formatCompactCurrency = (value: number): string => {
  const abs = Math.abs(value)
  if (abs >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`
  if (abs >= 1_000) return `$${(value / 1_000).toFixed(1)}K`
  return `$${Math.round(value)}`
}

// Agent convention is to suffix external/non-investigated entities with
// "(ext.)"; we also catch the literal word. Used by NetworkChart's color
// scheme and the "Hide external" toggle in ChartArtifact.
export const EXTERNAL_NAME_PATTERN = /\(ext\.?\)|external/i

export const currencyTickFormatter = (value: number): string =>
  formatCurrency(value, { decimals: 1 })

// Formats a chart value per the agent-chosen unit: dollars for 'currency'
// ($1.2M / $3.4K), compact unitless for 'number' (1.2M / 3.4K counts).
export const formatChartValue = (
  value: number,
  format: 'currency' | 'number',
): string =>
  format === 'currency' ? formatCompactCurrency(value) : formatNumber(value)

export const dateTickFormatter = (date: string | Date): string =>
  formatDate(date, '%b %Y')

const BUCKET_YEAR = /^(\d{4})$/
const BUCKET_YEAR_MONTH = /^(\d{4})-(\d{2})$/
const BUCKET_YEAR_MONTH_DAY = /^(\d{4})-(\d{2})-(\d{2})/

// Strictly parses a chart bucket label into a local Date, or returns null when
// the label is not a date (e.g. a category like "Checking (4567) — Mar 26").
// Unlike `new Date(...)`, this never guesses: a non-date string yields null
// instead of a bogus year. (V8 reads a bare "Mar 26" as the year 2001, which
// would otherwise flip a categorical bar chart onto a date axis.) Parses
// year-only and year-month buckets in local time so they don't drift a day
// across timezones.
export const parseBucketDate = (value: string): Date | null => {
  const yearMonthDay = BUCKET_YEAR_MONTH_DAY.exec(value)
  if (yearMonthDay) {
    return new Date(
      Number(yearMonthDay[1]),
      Number(yearMonthDay[2]) - 1,
      Number(yearMonthDay[3]),
    )
  }
  const yearMonth = BUCKET_YEAR_MONTH.exec(value)
  if (yearMonth) {
    return new Date(Number(yearMonth[1]), Number(yearMonth[2]) - 1, 1)
  }
  const year = BUCKET_YEAR.exec(value)
  if (year) return new Date(Number(year[1]), 0, 1)
  return null
}

export type DateGranularity = 'year' | 'month' | 'day'

const isYearBoundary = (date: Date): boolean =>
  (date.getMonth() === 0 && date.getDate() === 1) ||
  (date.getMonth() === 11 && date.getDate() === 31)

const isFirstOfMonth = (date: Date): boolean => date.getDate() === 1

// Decides the coarsest bucket a set of chart dates all sit on, so axis ticks
// and tooltips drop noise: a chart bucketed by year shows "2020", by month
// "Dec 2020", finer shows the full date. Bucket dates land on boundaries (the
// agent uses the first of the month or the start/end of the year), which is
// the signal we read.
export const inferDateGranularity = (dates: Date[]): DateGranularity => {
  if (dates.length === 0) return 'day'
  if (dates.every(isYearBoundary)) return 'year'
  if (dates.every(isFirstOfMonth)) return 'month'
  return 'day'
}

const GRANULARITY_FORMAT: Record<DateGranularity, string> = {
  year: '%Y',
  month: '%b %Y',
  day: '%b %d, %Y',
}

// A tick/label formatter for the inferred bucket granularity. Same string|Date
// input as the other tick formatters; a non-date string is returned as-is
// (it's a category label, not a date).
export const granularityDateFormatter =
  (granularity: DateGranularity) =>
  (date: string | Date): string => {
    const parsed = typeof date === 'string' ? parseBucketDate(date) : date
    if (!parsed || Number.isNaN(parsed.getTime())) {
      return typeof date === 'string' ? date : String(date)
    }
    return d3.timeFormat(GRANULARITY_FORMAT[granularity])(parsed)
  }

export const percentTickFormatter = (value: number): string =>
  formatPercent(value, 0)
